import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Mic, Square, Upload, Film, Check, Play, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useSeo } from "@/lib/seo";

export default function Contribute() {
  const { shareToken } = useParams();
  const [event, setEvent] = useState(null);
  useSeo({
    title: event?.title ? `You're invited: ${event.title}` : "You're invited",
    description: "Record a short video or photo message and become part of a shared cinematic tribute — no login required.",
    path: `/c/${shareToken}`,
    noindex: true,
  });
  const [name, setName] = useState("");
  const [promptIdx, setPromptIdx] = useState(0);
  const [note, setNote] = useState("");
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState("");
  const [recording, setRecording] = useState(false);
  const [stream, setStream] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submittedIds, setSubmittedIds] = useState([]);
  const videoRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    axios.get(`${API}/public/events/${shareToken}`).then((r) => setEvent(r.data))
      .catch(() => toast.error("Event not found"));
  }, [shareToken]);

  const startStream = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        videoRef.current.play();
      }
    } catch (e) { toast.error("Camera access denied"); }
  };

  const startRecording = async () => {
    if (!stream) await startStream();
    const s = stream || (await navigator.mediaDevices.getUserMedia({ video: true, audio: true }));
    if (!stream) setStream(s);
    chunksRef.current = [];
    const mr = new MediaRecorder(s, { mimeType: "video/webm" });
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      setRecordedUrl(URL.createObjectURL(blob));
      s.getTracks().forEach((t) => t.stop());
      setStream(null);
    };
    recorderRef.current = mr;
    mr.start();
    setRecording(true);
  };

  const stopRecording = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setRecordedBlob(f);
    setRecordedUrl(URL.createObjectURL(f));
  };

  const isPhoto = recordedBlob && (recordedBlob.type || "").startsWith("image/");

  const upload = async () => {
    if (!name.trim()) { toast.error("Please add your name"); return; }
    if (!recordedBlob) { toast.error("Record or upload a clip first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("contributor_name", name.trim());
      fd.append("prompt", event.prompts[promptIdx]);
      fd.append("note", note);
      const ext = recordedBlob.type.includes("mp4") ? "mp4" : (recordedBlob.type.includes("image/") ? (recordedBlob.type.split("/")[1] || "jpg") : "webm");
      fd.append("file", recordedBlob, `clip.${ext}`);
      const { data } = await axios.post(`${API}/public/events/${shareToken}/submissions`, fd);
      toast.success("Thank you — your voice is in the film");
      setSubmittedIds((arr) => [...arr, data.id]);
      // reset for next prompt
      setRecordedBlob(null); setRecordedUrl(""); setNote("");
      if (promptIdx + 1 < event.prompts.length) setPromptIdx(promptIdx + 1);
    } catch (e) {
      toast.error("Upload failed. Try a shorter clip.");
    } finally { setUploading(false); }
  };

  if (!event) {
    return <div className="min-h-screen bg-[var(--em-bg)] grid place-items-center text-[var(--em-ink-soft)]">Loading…</div>;
  }

  const done = submittedIds.length > 0 && promptIdx === event.prompts.length - 1 && !recordedBlob;

  return (
    <div className="min-h-screen bg-[var(--em-bg)] text-[var(--em-ink)]">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full bg-[var(--em-clay)] grid place-items-center text-white"><Film size={16} /></span>
          <span className="font-serif-em text-xl">Chapter</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-10">
          <p className="font-mono-label text-[var(--em-clay)]">You're invited</p>
          <h1 className="font-serif-em text-4xl md:text-5xl mt-3 leading-tight" data-testid="contrib-event-title">{event.title}</h1>
          {event.description && <p className="mt-3 text-[var(--em-ink-soft)] max-w-2xl">{event.description}</p>}
        </motion.div>

        {!name && (
          <Card className="bg-white border-[var(--em-line)] mt-8">
            <CardContent className="p-6">
              <Label className="font-mono-label">Your name</Label>
              <Input data-testid="contrib-name-input" placeholder="Who are we hearing from?" onChange={(e) => setName(e.target.value)} className="mt-2 h-12 bg-[var(--em-bg)]" />
              <p className="text-sm text-[var(--em-ink-soft)] mt-3">Shown in the final film next to your message.</p>
            </CardContent>
          </Card>
        )}

        {name && !done && (
          <div className="mt-8">
            <div className="flex items-center gap-3">
              <span className="font-mono-label text-[var(--em-ink-soft)]">Prompt {promptIdx + 1} / {event.prompts.length}</span>
              <div className="h-1 bg-[var(--em-line)] flex-1 rounded-full overflow-hidden">
                <div className="h-full bg-[var(--em-clay)]" style={{ width: `${((promptIdx) / event.prompts.length) * 100}%` }} />
              </div>
            </div>
            <h2 className="font-serif-em text-3xl mt-4" data-testid="contrib-prompt">{event.prompts[promptIdx]}</h2>

            <Card className="bg-white border-[var(--em-line)] mt-6">
              <CardContent className="p-6">
                <div className="aspect-video bg-black rounded-xl overflow-hidden grid place-items-center relative">
                  {recordedUrl ? (
                    isPhoto ? (
                      <img src={recordedUrl} alt="preview" className="w-full h-full object-contain bg-black" data-testid="contrib-preview-img" />
                    ) : (
                      <video src={recordedUrl} controls className="w-full h-full object-contain bg-black" data-testid="contrib-preview" />
                    )
                  ) : stream ? (
                    <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-white/60 text-center">
                      <Mic size={28} className="mx-auto mb-2" />
                      <p className="text-sm">Record a clip, or upload a video / photo</p>
                    </div>
                  )}
                  {recording && <span className="absolute top-3 left-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full">REC</span>}
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                  {!recording && !recordedBlob && (
                    <Button data-testid="contrib-record-btn" onClick={startRecording} className="rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white btn-lift">
                      <Mic className="mr-2 h-4 w-4" /> Record
                    </Button>
                  )}
                  {recording && (
                    <Button data-testid="contrib-stop-btn" onClick={stopRecording} variant="outline" className="rounded-full">
                      <Square className="mr-2 h-4 w-4" /> Stop
                    </Button>
                  )}
                  {!recording && (
                    <label className="inline-flex items-center gap-2 cursor-pointer">
                      <input data-testid="contrib-file-input" type="file" accept="video/*,image/*" onChange={onFile} className="hidden" />
                      <span className="inline-flex items-center h-10 px-5 rounded-full border border-[var(--em-line)] bg-white hover:bg-[var(--em-bg)]"><Upload className="mr-2 h-4 w-4" />Upload video / photo</span>
                    </label>
                  )}
                  {recordedBlob && !recording && (
                    <Button data-testid="contrib-retake-btn" onClick={() => { setRecordedBlob(null); setRecordedUrl(""); }} variant="ghost" className="rounded-full">Retake</Button>
                  )}
                </div>

                <div className="mt-5">
                  <Label className="font-mono-label">A short note (optional)</Label>
                  <Textarea data-testid="contrib-note-input" value={note} onChange={(e) => setNote(e.target.value)} className="mt-2 bg-[var(--em-bg)]" placeholder="A line that will appear with your clip." />
                </div>

                <div className="mt-6 flex items-center justify-between">
                  <Button data-testid="contrib-submit-btn" onClick={upload} disabled={uploading || !recordedBlob} className="rounded-full bg-[var(--em-night)] hover:bg-black text-white btn-lift">
                    {uploading ? "Uploading…" : (<><Check className="mr-2 h-4 w-4" />Send my message</>)}
                  </Button>
                  {promptIdx + 1 < event.prompts.length && (
                    <Button variant="ghost" data-testid="contrib-skip-btn" onClick={() => { setRecordedBlob(null); setRecordedUrl(""); setPromptIdx(promptIdx + 1); }}>
                      Skip <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {submittedIds.length > 0 && (
              <p className="text-sm text-[var(--em-ink-soft)] mt-3" data-testid="contrib-count">{submittedIds.length} message{submittedIds.length>1?"s":""} sent — thank you.</p>
            )}
          </div>
        )}

        {done && (
          <Card className="bg-white border-[var(--em-line)] mt-8" data-testid="contrib-done">
            <CardContent className="p-8 text-center">
              <p className="font-mono-label text-[var(--em-sage)]">Thank you</p>
              <h2 className="font-serif-em text-3xl mt-3">Your voice is part of the film.</h2>
              <p className="text-[var(--em-ink-soft)] mt-2">Want to watch how it's coming together?</p>
              <Link to={`/c/${shareToken}/play`}>
                <Button className="mt-6 rounded-full bg-[var(--em-clay)] hover:bg-[var(--em-clay-deep)] text-white btn-lift"><Play className="mr-2 h-4 w-4" />Watch the reel</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
